// Optimistic overlay for area/space/project writes, the container counterpart of the task outbox
// overlay. Every container write goes through `writeContainers`: its patch shows immediately in the
// sidebar, page headers, breadcrumbs, pickers and search, and stays layered over the cached bootstrap
// until a bootstrap fetched *after* the write was acknowledged arrives. That makes the visible state
// independent of refetch timing: a slow, cancelled, or stale in-flight fetch can't put an old name
// back. A failed write drops its patch, which is the rollback.
import { useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { invalidateContainers, qk } from "./cache";

export type ContainerKind = "area" | "space" | "project";

export interface ContainerChange {
  kind: ContainerKind;
  id: string;
  patch: Record<string, unknown>;
}

export interface OverlayEntry extends ContainerChange {
  seq: number;
  /** When the server acknowledged the write; null while it is in flight. */
  confirmedAt: number | null;
}

class ContainerOverlayStore {
  private entries: OverlayEntry[] = [];
  private seq = 0;
  private listeners = new Set<() => void>();
  private snapshot: readonly OverlayEntry[] = [];

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => void this.listeners.delete(l);
  };
  getSnapshot = () => this.snapshot;

  private emit() {
    this.snapshot = [...this.entries];
    this.listeners.forEach((l) => l());
  }

  add(change: ContainerChange): number {
    const seq = ++this.seq;
    this.entries.push({ ...change, seq, confirmedAt: null });
    this.emit();
    return seq;
  }

  confirm(seq: number, at = Date.now()) {
    this.entries = this.entries.map((e) => (e.seq === seq ? { ...e, confirmedAt: at } : e));
    this.emit();
  }

  drop(seq: number) {
    this.entries = this.entries.filter((e) => e.seq !== seq);
    this.emit();
  }

  /** Forget confirmed patches that a bootstrap requested at `fetchStartedAt` already reflects. */
  prune(fetchStartedAt: number) {
    const keep = this.entries.filter((e) => !isReflected(e, fetchStartedAt));
    if (keep.length === this.entries.length) return;
    this.entries = keep;
    this.emit();
  }

  /** Tests only. */
  reset() {
    this.entries = [];
    this.emit();
  }
}

const isReflected = (e: OverlayEntry, fetchStartedAt: number) => e.confirmedAt !== null && e.confirmedAt < fetchStartedAt;

export const containerOverlay = new ContainerOverlayStore();

export function useContainerOverlay(): readonly OverlayEntry[] {
  return useSyncExternalStore(containerOverlay.subscribe, containerOverlay.getSnapshot, containerOverlay.getSnapshot);
}

/** Layer pending and not-yet-refetched patches over server records, oldest first. */
export function applyContainerOverlay<T extends { id: string }>(
  kind: ContainerKind,
  list: T[],
  entries: readonly OverlayEntry[],
  fetchStartedAt: number,
): T[] {
  const live = entries.filter((e) => e.kind === kind && !isReflected(e, fetchStartedAt));
  if (live.length === 0) return list;
  return list.map((c) => live.reduce<T>((out, e) => (e.id === c.id ? { ...out, ...e.patch } : out), c));
}

/** Bootstrap data carries the time its request began, so the overlay knows what it already includes. */
export type Stamped<T> = T & { fetchStartedAt?: number };
export const fetchStartedAt = (data: { fetchStartedAt?: number } | undefined) => data?.fetchStartedAt ?? 0;

/** Cached Search responses may be older than bootstrap; retain patches until both reflect them. */
export function pruneContainerOverlay(qc: QueryClient) {
  const sources = [qc.getQueryData<Stamped<object>>(qk.bootstrap), ...qc.getQueriesData<Stamped<object>>({ queryKey: ["search"] }).map(([, data]) => data)];
  containerOverlay.prune(Math.min(...sources.filter((data) => data !== undefined).map(fetchStartedAt)));
}

const writeTails = new Map<string, Promise<void>>();

/**
 * Apply `changes` optimistically, run `request`, then refetch. Throws the request's error after
 * removing the patches.
 */
export async function writeContainers<T>(qc: QueryClient, request: () => Promise<T>, changes: ContainerChange[] = []): Promise<T> {
  // Reserve the turn synchronously, but show every queued patch immediately. An older delayed
  // response must finish before a newer request can be acknowledged and pruned from the overlay.
  const keys = [...new Set(changes.map((c) => `${c.kind}:${c.id}`))];
  const prior = Promise.all(keys.map((key) => writeTails.get(key)));
  let release!: () => void;
  const tail = new Promise<void>((resolve) => { release = resolve; });
  keys.forEach((key) => writeTails.set(key, tail));
  let seqs: number[] = [];
  try {
    if (changes.length) await qc.cancelQueries({ queryKey: qk.bootstrap });
    seqs = changes.map((c) => containerOverlay.add(c));
    await prior;
    const result = await request();
    const at = Date.now();
    seqs.forEach((s) => containerOverlay.confirm(s, at));
    return result;
  } catch (e) {
    seqs.forEach((s) => containerOverlay.drop(s));
    throw e;
  } finally {
    void invalidateContainers(qc);
    release();
    keys.forEach((key) => { if (writeTails.get(key) === tail) writeTails.delete(key); });
  }
}
